import { Component, Element, h, Prop, Watch } from "@stencil/core";
import { DEFAULT_PAGE_BACKGROUND_COLOR } from "../../constants/colors";
import { BackgroundColor } from "./constants/background-types";
import pfx from "../../utils/style-class-prefix-helper";

/**
 * @slot [unnamed] - Holds any number of HTML elements
 */
@Component({
  tag: "eon-ui-background",
  styleUrl: "background.scss",
  shadow: true
})
export class Background {
  @Element() hostElement: HTMLEonUiBackgroundElement;

  /**
   * The name of the background color to set. Child components might change their appearance based on this color.
   *
   * Therefore it is important to make this property not mutable. Users must recreate the Background component
   * to change the color of the background. This way, the child components will also rerender and are able to change
   * their color based on the background color change. If this property would be mutable, automatic color changing of components
   * is not possible without searching the dom from the background component or checking in an intervall, which might be costly.
   */
  @Prop({ reflect: true }) color: BackgroundColor = DEFAULT_PAGE_BACKGROUND_COLOR;

  /**
   * If set to true, the background div will not be colored using CSS.
   * Use this to draw a visually transparent background that still sets the style context for child elements.
   */
  @Prop() invisible: boolean = false;

  componentWillLoad() {
    this.setCssVariables();
  }

  @Watch("color")
  @Watch("invisible")
  private setCssVariables() {
    const color: string = `var(--${this.invisible ? "eon-transparent" : this.color})`;
    this.hostElement.style.setProperty("--bgColor", color);
  }

  render() {
    return (
      <div class={pfx("background")}>
        <slot />
      </div>
    );
  }
}
